import * as moment from "moment-timezone";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import { IDrop } from "src/core/drops/api/drops.api";
import {
  getDeliveryCutoff,
  getDeliveryRoute,
  getDeliveryWeekLeadTimes,
  getOrderDataRequired,
  getSingleOrder
} from "src/core/drops/utils/inherited-settings";
import { poNumberIsValid } from "src/core/orders/utils/orders";
import CreateOrderCalendar, { CreateOrderCalendarRef } from "src/core/shared/components/HeaderContext/CreateOrderCalendar";
import { StyledInput } from "src/core/shared/components/InputFields";
import { useDisabledDeliveryDate } from "src/core/shared/hooks/useDisabledDeliveryDate";
import { IStore } from "src/core/shared/store";
import { Modal } from "src/library/components/Modal";
import { Text } from "src/library/components/Text/Text";
import { hasOrders } from "src/library/utils";

import { useOrderVariant } from "../../hooks/useOrderVariant";
import ConfirmMultipleOrderModal from "./ConfirmMultipleOrderModal";

interface DayPickerModalProps {
  onConfirm: (day: Date, editBefore: boolean, cusotmerReference?: string) => void;
  handleDismiss: () => void;
  show: boolean;
  drop: IDrop;
  modalTitle?: string;
  orderVariantId: number
}

// types based on ModalViews value
interface BodyTitles {
  0: string,
  1: string
}

// number to be extendable
enum ModalViews {
  DeliveryDate = 0,
  PoNumber = 1
}

const OrderDayPickerModal: React.ReactFCWithChildren<DayPickerModalProps> = props => {
  const { drop } = props;
  const history = useHistory();
  const singleDailyDelivery = drop && getSingleOrder(drop.groupedSettings);
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings?.terminology }));
  const [ deliveryDate, setDeliveryDate ] = React.useState<Date | null>(null);
  const [ poNumber, setPONumber ] = React.useState<string>("");
  const [ poError, setPOError ] = React.useState<string | null>(null);
  const ordersCalendarRef = React.useRef<CreateOrderCalendarRef>(null);
  const getOrderVariant = useOrderVariant();

  const { orderDates, currentOrders } = React.useMemo(() => ({
    orderDates: ordersCalendarRef.current?.orderDates ?? [],
    currentOrders: ordersCalendarRef.current?.currentOrders ?? []
  }), [ ordersCalendarRef ]);

  const [ currentModalView, setCurrentModalView ] = React.useState<number>(ModalViews.DeliveryDate);
  const [ showMultipleOrderModal, setShowMultipleOrderModal ] = React.useState(false);
  const isDateDisabled = useDisabledDeliveryDate(drop);
  const [ storedEditBeforePlacingOrder, setStoredEditBeforePlacingOrder ] = React.useState(false);
  const orderDataRequired = drop && getOrderDataRequired(drop.groupedSettings);

  // set whether po is required or not
  const poRequired: boolean | undefined = React.useMemo(() => {
    if (drop) {
      if (typeof orderDataRequired?.orderPONumber === "boolean") {
        return orderDataRequired?.orderPONumber;
      } else {
        return orderDataRequired?.orderPONumber.required;
      }
    }

    return false;
  }, [ drop, orderDataRequired ]);

  // generate available modal body titles that will be set based on active modal view
  const bodyTitles: BodyTitles = React.useMemo(() => {
    return {
      0: `Please select a ${terminology?.delivery.singular.text} day for this ${terminology?.order.singular.text}`,
      1: "Please enter order PO number."
    };
  }, [ terminology ]);

  // Handle the primary action of the modal.
  const handlePrimaryAction = React.useCallback((editBeforePlacingOrder: boolean, confirmedMultiOrder?: boolean) => {
    if (!deliveryDate) {
      return;
    }
    const hasOrderOnDay = hasOrders(deliveryDate, orderDates);

    // if on first step and PO required, update modal view
    if (currentModalView === ModalViews.DeliveryDate && deliveryDate) {
      return setCurrentModalView(ModalViews.PoNumber);
    }

    // in last view, if valid execute callbacks
    if (currentModalView === ModalViews.PoNumber || (currentModalView === ModalViews.DeliveryDate && !poRequired)) {
      if (poRequired && poNumber.length === 0) {
        return setPOError("PO required.");
      }

      // If one-order-per-day setting is true, and an order exists:
      if (singleDailyDelivery && singleDailyDelivery.value === true && hasOrderOnDay) {
        const orderDay = orderDates.find(or => moment.utc(or.date).isSame(deliveryDate, "day"));

        if (orderDay) {
          history.push(`/orders/${orderDay.id}`, { edit: true });
        } else {
          history.push("/orders", { edit: true });
        }
      } else if (hasOrderOnDay && !confirmedMultiOrder) {
        setShowMultipleOrderModal(true);
      } else {
        props.onConfirm(deliveryDate, editBeforePlacingOrder, poNumber.trim());
      }

      props.handleDismiss();
    }
  }, [
    deliveryDate,
    currentModalView,
    poRequired,
    poNumber,
    singleDailyDelivery,
    props,
    orderDates,
    history
  ]);

  const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
  const deliveryRoute = getDeliveryRoute(drop.groupedSettings);
  const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);

  if (!leadTimes || !deliveryRoute || !defaultCutoff) {
    return null;
  }
  const isConfirm = currentModalView >= ModalViews.PoNumber;

  return (
    <>
      <Modal
        small
        actions={[
          {
            name: "Go back",
            action: () => {
            // return to last view if progress made otherwise close
              if (currentModalView > ModalViews.DeliveryDate) {
                setCurrentModalView(currentView => currentView - 1);
                poError && setPOError(null);
              } else {
                props.handleDismiss();
              }
            },
            $buttonType: "secondary"
          },
          {
            name: "Next",
            actions: isConfirm ? [
              {
                title: "Place order",
                action: () => {
                  setStoredEditBeforePlacingOrder(false);
                  handlePrimaryAction(false);
                }
              },
              {
                title: "Create as draft",
                action: () => {
                  setStoredEditBeforePlacingOrder(true);
                  handlePrimaryAction(true);
                }
              }
            ] : undefined,
            disabled: !deliveryDate || isDateDisabled(deliveryDate, currentOrders, getOrderVariant(props.orderVariantId)).isDisabled,
            action: () => handlePrimaryAction(false),
            $buttonType: "primary"
          }
        ]}
        backgroundAction={() => props.handleDismiss()}
        body={bodyTitles[ currentModalView ]}
        content={currentModalView === ModalViews.DeliveryDate ?
          (
            <CreateOrderCalendar
              drop={drop}
              handleDateChange={setDeliveryDate}
              orderVariantId={props.orderVariantId}
              ref={ordersCalendarRef}
            />
          ) :
          currentModalView === ModalViews.PoNumber ?
            (
              <>
                <StyledInput
                  onChange={e => {
                    const elImputValue = e.target.value;

                    if (drop) {
                      const poNumberError = poNumberIsValid(e.target.value, drop);

                      setPOError(poNumberError);
                    }

                    setPONumber(elImputValue);
                  }}
                  value={poNumber}
                  placeholder={`Purchase Order No.${poRequired ? " *" : ""}`}
                />

                {poError && (
                  <Text color="error">
                    {poError}
                  </Text>
                )}
              </>
            ) :
          // return undefined instead of null as default if currentModalView not recognised as requested by content prop
          // looping lint format error...
          // eslint-disable-next-line react/jsx-indent
            undefined}
        type="confirm"
        title={props.modalTitle || "Select Order Date"}
        show={props.show}
      />

      {deliveryDate && (
        <ConfirmMultipleOrderModal
          onDismiss={() => setShowMultipleOrderModal(false)}
          open={showMultipleOrderModal}
          onConfirm={() => handlePrimaryAction(storedEditBeforePlacingOrder, true)}
          orderDates={orderDates}
          selectedDay={deliveryDate}
          terminology={terminology}
        />
      )}
    </>
  );
};

export default OrderDayPickerModal;
